import { useEffect, useState, useRef } from 'react'
import { EmailForm } from '@/components/email-drafter/email-form'
import { PromptsModal } from '@/components/email-drafter/prompts-modal'
import { HistoryPanel } from '@/components/email-drafter/history-panel'
import { ResponseViewer } from '@/components/email-drafter/response-viewer'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Settings, Download, Upload, BarChart3, Mail, History, Menu } from 'lucide-react'
import { useEmailDrafterStore } from '@/stores/email-drafter'

export function EmailDrafter() {
  const [showPrompts, setShowPrompts] = useState(false)
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { stats, loadHistory, exportData, importData } = useEmailDrafterStore()

  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      await importData(file)
      // Reset the input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2 sm:gap-4">
              <Mail className="h-6 w-6 sm:h-8 sm:w-8 text-primary flex-shrink-0" />
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold">Email Drafter Testing</h1>
                <p className="text-sm sm:text-base text-muted-foreground hidden sm:block">
                  Testen und optimieren Sie Email-Antworten mit dem Mission Solar AI System
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => setShowPrompts(true)}
                className="text-sm sm:text-base"
              >
                <Settings className="mr-0 sm:mr-2 h-4 w-4" />
                <span className="hidden sm:inline">System Prompts</span>
              </Button>
              <Button
                variant="outline"
                onClick={exportData}
                className="text-sm sm:text-base"
              >
                <Download className="mr-0 sm:mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Export</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="text-sm sm:text-base"
              >
                <Upload className="mr-0 sm:mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Import</span>
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImport}
                className="hidden"
              />
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Gesamt Tests
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalTests}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Erfolgreiche Tests
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {stats.successTests}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Erfolgsquote
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.totalTests > 0
                    ? `${Math.round((stats.successTests / stats.totalTests) * 100)}%`
                    : '-'}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Ø Antwortzeit
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.averageResponseTime
                    ? `${(stats.averageResponseTime / 1000).toFixed(1)}s`
                    : '-'}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Info Banner */}
          <Card className="mb-6 border-blue-200 bg-blue-50/50 dark:bg-blue-950/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Über dieses Tool
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">
                Diese Schnittstelle ermöglicht es, Email-Antworten mit verschiedenen System-Prompts zu testen.
                Die Antworten werden über Make.com Webhooks generiert und können zur Qualitätssicherung
                und Optimierung verwendet werden. Alle Tests werden lokal gespeichert und können exportiert werden.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Mobile History Button */}
        <div className="lg:hidden mb-4">
          <Sheet open={mobileHistoryOpen} onOpenChange={setMobileHistoryOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="w-full">
                <History className="mr-2 h-4 w-4" />
                Test Verlauf anzeigen
                {stats.totalTests > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {stats.totalTests}
                  </Badge>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md p-0">
              <SheetHeader className="p-4 border-b">
                <SheetTitle>Test Verlauf</SheetTitle>
              </SheetHeader>
              <div className="h-[calc(100vh-80px)]">
                <HistoryPanel />
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <EmailForm />
            <ResponseViewer />
          </div>
          {/* Desktop History Panel */}
          <div className="hidden lg:block lg:col-span-1">
            <HistoryPanel />
          </div>
        </div>

        {/* Prompts Modal */}
        <PromptsModal open={showPrompts} onOpenChange={setShowPrompts} />
      </div>
    </div>
  )
}